/* =====================================================================
   geo.js — pure geo helpers (no Google dependency, safe to unit-test)
   ===================================================================== */

/* Ported from the delivery-boy app's validator
   (BringFood_DB_Android/src/screens/delivery/InfoUser.js:21-31).
   Its comment records why the obvious `lat > 0 && lng > 0` test is wrong:
   it broke UK addresses, whose longitude is NEGATIVE, and silently fell
   through to a billable geocode. Also rejects the 0,0 "Null Island"
   placeholder — every web order ever created carries a literal
   latitude:0/longitude:0, so presence is NOT proof of a real fix. */
const present = (v) => v !== null && v !== undefined && v !== '' && typeof v !== 'boolean';

export const validCoordinates = (lat, lng) => {
  if (!present(lat) || !present(lng)) return false;
  const la = Number(lat), ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return false;
  if (la === 0 && ln === 0) return false;            // Null Island placeholder
  return la >= -90 && la <= 90 && ln >= -180 && ln <= 180;
};

/* Distance in km. Deliberately the SAME equirectangular approximation the
   admin uses (deliveras.service.ts:76-77) so the web and the dispatcher
   agree on which addresses are "too far"; accurate enough well beyond the
   30 km decision boundary. */
export const distanceKm = (aLat, aLng, bLat, bLng) => {
  const dLat = (Number(aLat) - Number(bLat)) * 111;
  const dLng = (Number(aLng) - Number(bLng)) * 111 * Math.cos(Number(bLat) * Math.PI / 180);
  return Math.sqrt(dLat * dLat + dLng * dLng);
};

/* Hard reject radius. A courier must never be dispatched to a lookalike
   street in another prefecture: the bias below is only a hint, THIS is the
   guarantee (see the hoodie #28 incident referenced in deliveras.service.ts:50-53). */
export const MAX_DELIVERY_KM = 30;

/* ~15 km box around the venue, used to bias autocomplete/geocoding results. */
export const biasBox = (venueLat, venueLng) => {
  if (!validCoordinates(venueLat, venueLng)) return null;
  const lat = Number(venueLat), lng = Number(venueLng);
  return { south: lat - 0.15, west: lng - 0.2, north: lat + 0.15, east: lng + 0.2 };
};

/* settings.country holds the platform's own codes ('el' for Greece, 'uk'/'gb'
   for the UK — cf. feathers-server soap.class.js:25). Google wants ISO 3166-1
   alpha-2, so 'el' must become 'GR'. The admin hardcodes region:'gr'
   (deliveras.service.ts:67), which would bias UK tenants to Greece. */
export const regionCode = (country) => {
  const c = `${country ?? ''}`.trim().toLowerCase();
  if (!c || c === 'el' || c === 'gr') return 'GR';   // unset => legacy Greek tenant
  if (c === 'uk' || c === 'gb') return 'GB';
  return c.length === 2 ? c.toUpperCase() : 'GR';
};

/* Accent/case-insensitive comparison, so a Google locality ("Αθήνα", "ATHENS")
   matches a tenant city entry regardless of accents or case. NFD splits the
   accent off the base letter; stripping the combining marks then works for
   Greek and Latin alike. */
export const normalizeName = (value) =>
  `${value ?? ''}`
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/* Reconcile a geocoded locality against the tenant's OWN city list.
   This matters because delivery pricing and order submission are city-ID
   based (Checkout.js findCity/findTransportation + the `+v.city > 0` guard):
   an address that does not map to an ENABLED settings.citys entry cannot be
   priced or submitted, so we must resolve an id or report the failure —
   never silently fall back to a hardcoded id the way the RN app does
   (BringFood_Android AddressData.js:325).
   @param candidates ordered list of locality-ish strings from Google
   @returns {{id:number, label:string}|null} */
export const matchTenantCity = (candidates, citys, pickLabel) => {
  const enabled = (citys || []).filter(c => c?.enabled);
  if (!enabled.length) return null;
  for (const candidate of (candidates || []).filter(Boolean)) {
    const needle = normalizeName(candidate);
    if (!needle) continue;
    const hit = enabled.find(c =>
      normalizeName(c?.name) === needle || normalizeName(c?.nameEnglish) === needle);
    if (hit) return { id: +hit.id, label: pickLabel ? pickLabel(hit) : (hit.name || hit.nameEnglish) };
  }
  // Second pass: tolerate "Athens 105 62" / "Chalandri, Attica" style values.
  for (const candidate of (candidates || []).filter(Boolean)) {
    const needle = normalizeName(candidate);
    if (needle.length < 3) continue;
    const hit = enabled.find(c => {
      const a = normalizeName(c?.name), b = normalizeName(c?.nameEnglish);
      return (a && (needle.includes(a) || a.includes(needle)))
          || (b && (needle.includes(b) || b.includes(needle)));
    });
    if (hit) return { id: +hit.id, label: pickLabel ? pickLabel(hit) : (hit.name || hit.nameEnglish) };
  }
  return null;
};

/* Normalise Google address components (new Places API uses
   {types, longText, shortText}; the legacy one uses {types, long_name,
   short_name}) into a plain {type: value} lookup.
   ALWAYS read by `types` — never by array index. The RN app indexes
   positionally (AddressData.js:293-298: [0]=number, [1]=street, [5]||[6]=postcode)
   which misparses UK addresses; do not copy that. */
export const componentsByType = (components) => {
  const out = {};
  for (const c of components || []) {
    const long = c.longText ?? c.long_name;
    const short = c.shortText ?? c.short_name;
    for (const t of c.types || []) {
      if (out[t] === undefined) out[t] = { long, short };
    }
  }
  return out;
};

/* Pull the address fields the app stores out of that lookup. */
export const extractAddress = (components) => {
  const by = componentsByType(components);
  const val = (t) => by[t]?.long ?? '';
  return {
    street: val('route'),
    streetNumber: by['street_number']?.short || val('street_number'),
    postcode: val('postal_code'),
    // Locality naming varies by country: 'locality' in Greece, 'postal_town'
    // in much of the UK, with the admin levels as further fallbacks.
    cityCandidates: [
      val('locality'),
      val('postal_town'),
      val('administrative_area_level_3'),
      val('administrative_area_level_2'),
      val('sublocality'),
      val('neighborhood')
    ].filter(Boolean),
    country: by['country']?.short || ''
  };
};
